import React, { useState, useEffect } from 'react';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { Link as RouterLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Link,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import { Maybe, YouTubeProfile } from '../../types/generated/graphql';
import _ from 'lodash';
import ProfileSelector from './ProfileSelector';
import { useGetMyYouTubeProfiles } from '../../api/graphql_profiles';
import Avatar from '@mui/material/Avatar';
// Icon set kept deliberately small. The previous nav was over-iconed
// — a Home next to the wordmark (redundant), Person on both
// "Profiles" and "Account" (visually duplicate), a SmartToy on
// "Agents" (cartoonish for an admin tool), and matching icons next
// to every label that already explained itself. The remaining
// imports are the ones that earn their pixels:
//   - KeyboardArrowDown: chevron, signals "this is a dropdown"
//   - MenuIcon: mobile hamburger, no other affordance
//   - PersonIcon: profile-list avatar fallback only
//   - LogoutIcon: universal logout pattern
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { auth } from '../../firebase';
import { setGlobalNavigate } from '../../api/graphql';
import { ProfileLivenessPills } from './ProfileLivenessPills';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

// Section headers in the Admin dropdown — uppercase, slate, no
// pointer events so they read as labels rather than clickable items.
const adminSectionHeaderSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  color: '#64748b',
  bgcolor: '#f8fafc',
  lineHeight: '28px',
  py: 0,
  pl: 1.5,
};

const Layout: React.FC<Props> = ({ client }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // `Layout` sits at `/`, but `:pid` is defined on the nested
  // `/profile/:pid` route — React Router v6 does NOT propagate
  // child route params up to a parent route component, so
  // `useParams().pid` is always undefined here. Parse it off
  // the pathname instead so the "Your decisions" link + the
  // active-profile highlight in the Account menu actually work
  // when the user is on a profile page.
  const pidMatch = location.pathname.match(/^\/profile\/([^/]+)/);
  const pid =
    pidMatch && pidMatch[1] !== 'admin' ? pidMatch[1] : undefined;
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  // Inline profiles in the Account dropdown — one fewer click to switch
  // profiles. Only fetches once the user's authed (parent effect calls the
  // ListProfiles page otherwise, so the request is free via Apollo cache).
  const {data: profilesData} = useGetMyYouTubeProfiles(client);
  const profiles: YouTubeProfile[] = _.compact(profilesData?.getMyYouTubeProfiles ?? []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    handleMenuClose();
    await signOut(auth);
    navigate('/');
  };

  const isAdminPage = location.pathname.startsWith('/admin');

  // Set up global navigation for Apollo error handler
  useEffect(() => {
    setGlobalNavigate(navigate);
  }, [navigate]);

  // Firebase Auth state listener with admin check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        // Get the ID token result to check custom claims
        try {
          const idTokenResult = await user.getIdTokenResult();
          const isAdminRole = idTokenResult.claims.role === 'admin';
          setIsAdmin(isAdminRole);
          console.log('User role:', idTokenResult.claims.role);
          console.log('User admin status:', isAdminRole);
        } catch (error) {
          console.error('Error checking admin claim:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return (
    <div>
      <AppBar position="static" color={isAdminPage ? "secondary" : "primary"} elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'white',
              fontWeight: 700,
              letterSpacing: '-0.01em'
            }}
          >
            GoodShowHQ
          </Typography>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {/* Top-level link to the profile list page. Without
                this, users could only reach the list by typing the
                URL — the Account dropdown showed individual profiles
                but not the list itself. */}
            <Button
              color="inherit"
              component={RouterLink}
              to="/profile"
              sx={{ mx: 1 }}
            >
              Profiles
            </Button>
            {/* Co-parenting circles — invite another guardian and
                manage the groups that share a set of kids. */}
            <Button
              color="inherit"
              component={RouterLink}
              to="/groups"
              sx={{ mx: 1 }}
            >
              Co-parents
            </Button>
            {/* User Menu */}
            <Box>
              <Button
                color="inherit"
                onClick={handleUserMenuOpen}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ mx: 1 }}
              >
                Account
              </Button>
              <Menu
                anchorEl={userMenuAnchorEl}
                open={Boolean(userMenuAnchorEl)}
                onClose={handleUserMenuClose}
                PaperProps={{ sx: { minWidth: 260 } }}
              >
                {/* Profile-scoped data lives on the profile home,
                    not in the account dropdown. The PROFILES
                    quick-switcher below is the only profile-aware
                    bit kept here. */}

                {/* Inline profiles — one click to switch, no Profiles page hop. */}
                <Typography
                  sx={{
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  }}
                >
                  Profiles
                </Typography>
                {profiles.length === 0 ? (
                  <MenuItem disabled sx={{ fontSize: 13, color: 'text.secondary' }}>
                    No profiles yet
                  </MenuItem>
                ) : (
                  profiles.map(p => {
                    const isActive = pid === p.id;
                    return (
                      <MenuItem
                        key={p.id}
                        component={RouterLink}
                        to={`/profile/${p.id}`}
                        onClick={handleUserMenuClose}
                        selected={isActive}
                        sx={{ fontSize: 13, py: 0.75 }}
                      >
                        <Avatar
                          src={p.thumbnails?.[0]?.url || undefined}
                          sx={{ width: 24, height: 24, mr: 1, fontSize: 12 }}
                        >
                          {(p.name || '').charAt(0).toUpperCase() || '?'}
                        </Avatar>
                        <Typography sx={{ fontSize: 13, fontWeight: isActive ? 600 : 400, flex: 1 }} noWrap>
                          {p.name || 'Untitled profile'}
                        </Typography>
                        {/* Two compact dots: YouTube + Desktop. The
                            chip pair would crowd the dropdown; dots
                            with hover tooltips give the same info
                            without the visual weight. */}
                        <Box sx={{ ml: 1 }}>
                          <ProfileLivenessPills profile={p} compact />
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>
            </Box>
            {isAdmin && (
              <Box>
                <Button
                  color="inherit"
                  onClick={handleAdminMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{ mx: 1 }}
                >
                  Admin
                </Button>
                <Menu
                  anchorEl={adminMenuAnchorEl}
                  open={Boolean(adminMenuAnchorEl)}
                  onClose={handleAdminMenuClose}
                >
                  <ListSubheader sx={adminSectionHeaderSx}>Operations</ListSubheader>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/operations/profiles"
                    onClick={handleAdminMenuClose}
                    sx={{ minWidth: '200px', pl: 3 }}
                  >
                    Profiles
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/operations/health"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Service health
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/operations/tasks"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Tasks &amp; jobs
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/operations/waitlist"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Waitlist
                  </MenuItem>

                  <ListSubheader sx={adminSectionHeaderSx}>AI</ListSubheader>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/ai/agents"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Agents
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/ai/scores"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Agent scores
                  </MenuItem>

                  <ListSubheader sx={adminSectionHeaderSx}>Content</ListSubheader>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/content/videos"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Videos
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/content/channel-feedback"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Channel feedback
                  </MenuItem>

                  <ListSubheader sx={adminSectionHeaderSx}>Costs</ListSubheader>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/costs/llm"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    LLM
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/costs/firestore"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Firestore
                  </MenuItem>

                  <ListSubheader sx={adminSectionHeaderSx}>Docs</ListSubheader>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/docs/cleanup-strategy"
                    onClick={handleAdminMenuClose}
                    sx={{ pl: 3 }}
                  >
                    Cleanup strategy
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>

          {/* Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={handleMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchorEl}
              open={Boolean(mobileMenuAnchorEl)}
              onClose={handleMenuClose}
            >
              {/* Profile-scoped data lives on the profile home,
                  not in the account dropdown. */}
              <MenuItem
                onClick={() => {
                  navigate('/profile');
                  handleMenuClose();
                }}
              >
                Profiles
              </MenuItem>
              <MenuItem
                onClick={() => {
                  navigate('/groups');
                  handleMenuClose();
                }}
              >
                Co-parents
              </MenuItem>
              {isAdmin && (
                <>
                  <Divider />
                  <ListSubheader sx={adminSectionHeaderSx}>Operations</ListSubheader>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/operations/profiles');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Profiles
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/operations/health');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Service health
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/operations/tasks');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Tasks &amp; jobs
                  </MenuItem>
                  <ListSubheader sx={adminSectionHeaderSx}>AI</ListSubheader>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/ai/agents');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Agents
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/ai/scores');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Agent scores
                  </MenuItem>
                  <ListSubheader sx={adminSectionHeaderSx}>Content</ListSubheader>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/content/videos');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Videos
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/content/channel-feedback');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Channel feedback
                  </MenuItem>
                  <ListSubheader sx={adminSectionHeaderSx}>Costs</ListSubheader>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/costs/llm');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    LLM
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/costs/firestore');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Firestore
                  </MenuItem>
                  <ListSubheader sx={adminSectionHeaderSx}>Docs</ListSubheader>
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/docs/cleanup-strategy');
                      handleMenuClose();
                    }}
                    sx={{ pl: 4 }}
                  >
                    Cleanup strategy
                  </MenuItem>
                </>
              )}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <main>
        <Container maxWidth={false} disableGutters> 
          <Outlet />
        </Container>
      </main>
    </div>
  );
};

export default Layout;
